import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link, useLocation, useNavigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import SimCardList from './components/SimCardList';
import SimCardDetail from './components/SimCardDetail';
import SimCardEditModal from './components/SimCardEditModal';
import ModemList from './components/ModemList';
import OwnerDetail from './components/OwnerDetail';
import OwnerSummary from './components/OwnerSummary';
import Dashboard from './components/Dashboard';
import LoginModal from './components/LoginModal';
import ConfigDialog from './components/ConfigDialog';
import WorkContainer from './components/WorkContainer';
import PSIContainer from './components/psi/PSIContainer';
import CameraPage from './components/CameraPage';
import BarcodeTest from './components/BarcodeTest';
import BluetoothTest from './components/BluetoothTest';
import VoiceMessagingPage from './components/VoiceMessagingPage';
import { Toaster } from 'react-hot-toast';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { getEnv } from './config';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { WebRTCProvider, useWebRTC } from './contexts/WebRTCContext';
import { AIModelProvider, useAIModel } from './contexts/AIModelContext';
import { EmployeeProvider } from './contexts/EmployeeContext';
import { ISimCard } from './types/api';
import LanguageSwitcher from './components/LanguageSwitcher';
import { useTranslation } from 'react-i18next';
import './App.css';
import packageJson from '../package.json';

// Landing Page
import LandingPage from './pages/landing/LandingPage';
import ProductCategoryPage from './pages/landing/ProductCategoryPage';
import BlogDetailPage from './pages/landing/BlogDetailPage';

// Blog Components
import { BlogManagement } from './components/blog';
import MqttChatViewer from './components/chat/MqttChatViewer';
import LineChatMonitor from './components/line/LineChatMonitor';

// BOM Components
import { BOMPage, ProductDetail } from './components/bom';
import PhaseTransition from './components/bom/PhaseTransition';
import ProductEdit from './components/bom/ProductEdit';

// Project Components
import { ProjectContainer } from './components/project';

// MES Components
import { MOListPage, MODetailPage } from './components/mes';
import { WorkflowListPage, WorkflowEditorPage, WorkflowRunPage, ExecutionResultsPage } from './components/docflow';

// WebFlash Components
import { WebFlashPage } from './components/webflash';

// Pomodoro Timer
import { PomodoroPage, PomodoroWidget } from './components/pomodoro';
import MistakeNotebookPage from './pages/mistake-notebook/MistakeNotebookPage';
import FlowButtonLayer from './components/flowbutton/FlowButtonLayer';
import { PomodoroProvider } from './contexts/PomodoroContext';
import { CartProvider } from './contexts/CartContext';

// E-Commerce Pages
import CartPage from './pages/cart/CartPage';
import CheckoutPage from './pages/checkout/CheckoutPage';
import CheckoutSuccessPage from './pages/checkout/CheckoutSuccessPage';

// UI Components
import AppSidebar from './components/ui/AppSidebar';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 30000, // 30 seconds
    },
  },
});

// AI Model Selector Component
const AIModelSelector: React.FC = () => {
  const { selectedModel, setSelectedModel, models } = useAIModel();
  const [isOpen, setIsOpen] = useState(false);

  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'fast':
        return 'text-green-500';
      case 'balanced':
        return 'text-blue-500';
      case 'powerful':
        return 'text-purple-500';
      default:
        return 'text-gray-500';
    }
  };

  const getTierBadge = (tier: string) => {
    switch (tier) {
      case 'fast':
        return 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300';
      case 'balanced':
        return 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300';
      case 'powerful':
        return 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300';
      default:
        return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors text-sm ${getTierColor(selectedModel.tier)}`}
        title={`AI Model: ${selectedModel.name} - ${selectedModel.description}`}
      >
        {/* AI/Sparkle Icon */}
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
        <span className="hidden sm:inline font-medium">{selectedModel.name}</span>
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <>
          {/* Backdrop - darker on mobile for bottom sheet */}
          <div
            className="fixed inset-0 z-40 bg-black/20 sm:bg-transparent"
            onClick={() => setIsOpen(false)}
          />
          {/* Mobile: Bottom Sheet, Desktop: Dropdown */}
          <div className="fixed inset-x-0 bottom-0 sm:absolute sm:inset-auto sm:right-0 sm:top-full sm:mt-2 sm:w-56 bg-white dark:bg-gray-800 rounded-t-xl sm:rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50 max-h-[70vh] sm:max-h-none overflow-hidden">
            {/* Mobile drag handle */}
            <div className="sm:hidden flex justify-center pt-2 pb-1">
              <div className="w-10 h-1 bg-gray-300 dark:bg-gray-600 rounded-full" />
            </div>
            <div className="p-3 sm:p-2 border-b border-gray-200 dark:border-gray-700">
              <p className="text-sm sm:text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                AI Model for Web Search
              </p>
            </div>
            <div className="py-1 overflow-y-auto">
              {models.map((model) => (
                <button
                  key={model.id}
                  onClick={() => {
                    setSelectedModel(model);
                    setIsOpen(false);
                  }}
                  className={`w-full px-4 sm:px-3 py-3 sm:py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between ${
                    selectedModel.id === model.id ? 'bg-gray-50 dark:bg-gray-700/50' : ''
                  }`}
                >
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900 dark:text-white text-base sm:text-sm">
                        {model.name}
                      </span>
                      <span className={`text-xs px-1.5 py-0.5 rounded ${getTierBadge(model.tier)}`}>
                        {model.tier}
                      </span>
                    </div>
                    <p className="text-sm sm:text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      {model.description}
                    </p>
                  </div>
                  {selectedModel.id === model.id && (
                    <svg className="w-5 h-5 sm:w-4 sm:h-4 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </button>
              ))}
            </div>
            {/* Mobile close button */}
            <div className="sm:hidden p-3 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setIsOpen(false)}
                className="w-full py-2.5 text-center text-gray-600 dark:text-gray-400 font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// Navigation Header Component
interface NavigationHeaderProps {
  onLoginClick: () => void;
}

// Theme Toggle Button Component
const ThemeToggle: React.FC = () => {
  const { isDark, toggleTheme } = useTheme();

  return (
    <button
      onClick={toggleTheme}
      className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
      title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
    >
      {isDark ? (
        <svg className="w-5 h-5 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      ) : (
        <svg className="w-5 h-5 text-gray-700 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
        </svg>
      )}
    </button>
  );
};

// Voice Toggle Button Component
const VoiceToggle: React.FC = () => {
  const { isEnabled, connectionState, connect, disconnect, pendingCount } = useWebRTC();
  const { isAuthenticated } = useAuth();

  if (!isAuthenticated) {
    return null;
  }

  const handleToggle = () => {
    if (isEnabled) {
      disconnect();
    } else {
      connect();
    }
  };

  const getStatusColor = () => {
    if (!isEnabled) {
      return 'text-gray-400 dark:text-gray-500';
    }
    switch (connectionState) {
      case 'connected':
        return 'text-green-500';
      case 'connecting':
      case 'reconnecting':
        return 'text-yellow-500 animate-pulse';
      case 'error':
        return 'text-red-500';
      default:
        return 'text-gray-400 dark:text-gray-500';
    }
  };

  const getTitle = () => {
    if (!isEnabled) {
      return 'Voice calling disabled - Click to enable';
    }
    switch (connectionState) {
      case 'connected':
        return 'Voice calling enabled - Click to disable';
      case 'connecting':
        return 'Connecting...';
      case 'reconnecting':
        return 'Reconnecting...';
      case 'error':
        return 'Connection error - Click to retry';
      default:
        return 'Disconnected - Click to reconnect';
    }
  };

  const getStatusDotColor = () => {
    if (!isEnabled) {
      return 'bg-gray-400';
    }
    switch (connectionState) {
      case 'connected':
        return 'bg-green-500';
      case 'connecting':
      case 'reconnecting':
        return 'bg-yellow-500 animate-pulse';
      case 'error':
        return 'bg-red-500';
      default:
        return 'bg-gray-400';
    }
  };

  return (
    <button
      onClick={handleToggle}
      className={`relative p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors ${getStatusColor()}`}
      title={getTitle()}
    >
      {/* Microphone Icon - show slash when disabled */}
      {isEnabled ? (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
          />
        </svg>
      ) : (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2"
          />
        </svg>
      )}

      {/* Status indicator dot */}
      <span
        className={`absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-white dark:border-gray-800 ${getStatusDotColor()}`}
      />

      {/* Pending message count badge */}
      {pendingCount > 0 && (
        <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center text-xs font-bold text-white bg-red-500 rounded-full px-1">
          {pendingCount > 99 ? '99+' : pendingCount}
        </span>
      )}
    </button>
  );
};

const NavigationHeader: React.FC<NavigationHeaderProps> = ({ onLoginClick }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user, logout } = useAuth();
  const { t } = useTranslation(['auth', 'dashboard', 'common']);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isConfigOpen, setIsConfigOpen] = useState(false);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  // Only derick has access to config
  // TODO: deprecated — replace with role-based check from JWT when Google OAuth is used in production
  const canAccessConfig = user?.username === 'derick' || user?.username === 'april';

  const handleLogout = () => {
    logout();
    navigate('/');
    setIsMobileMenuOpen(false);
  };

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target as Node)) {
        setIsMobileMenuOpen(false);
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobileMenuOpen]);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  return (
    <>
      <header className="bg-white dark:bg-gray-800 shadow dark:shadow-gray-700 sticky top-0 z-40">
        <div className="max-w-screen-2xl mx-auto px-3 sm:px-6 lg:px-10">
          <div className="flex justify-between items-center py-3 sm:py-4">
            {/* Logo */}
            <div className="flex items-center min-w-0">
              <Link to="/app" className="flex items-center min-w-0">
                <div className="flex-shrink-0 flex items-center">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-blue-600 to-blue-800 rounded-lg flex items-center justify-center">
                    <span className="text-white font-bold text-lg sm:text-xl">F</span>
                  </div>
                  <h1 className="ml-2 sm:ml-3 text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
                    {t('dashboard:header.fluxconn')}
                  </h1>
                </div>
              </Link>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-2 lg:space-x-4">
              {/* Dashboard */}
              <Link
                to="/app"
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  location.pathname === '/app'
                    ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-200'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                {t('dashboard:title')}
              </Link>

              {/* Voice Toggle */}
              <VoiceToggle />

              {/* AI Model Selector */}
              <AIModelSelector />

              {/* Language Toggle */}
              <LanguageSwitcher />

              {/* Theme Toggle */}
              <ThemeToggle />

              {/* Config Button (derick only) */}
              {canAccessConfig && (
                <button
                  onClick={() => setIsConfigOpen(true)}
                  className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  title={t('common:config.title', 'System Configuration')}
                >
                  <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </button>
              )}

              {/* Login/Logout Button */}
              {isAuthenticated ? (
                <div className="flex items-center space-x-3">
                  <span className="hidden lg:inline text-sm text-gray-600 dark:text-gray-400">
                    {t('auth:welcome', { name: '' })}<span className="font-medium text-gray-900 dark:text-white">{user?.name}</span>
                  </span>
                  <button
                    onClick={handleLogout}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  >
                    {t('auth:logout')}
                  </button>
                </div>
              ) : (
                <button
                  onClick={onLoginClick}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
                >
                  {t('auth:login')}
                </button>
              )}
            </nav>

            {/* Mobile: Essential buttons + Hamburger */}
            <div className="flex md:hidden items-center space-x-2">
              {/* Theme Toggle - always visible */}
              <ThemeToggle />

              {/* Hamburger Menu Button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                aria-label="Toggle menu"
              >
                {isMobileMenuOpen ? (
                  <svg className="w-6 h-6 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg className="w-6 h-6 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        {isMobileMenuOpen && (
          <div
            ref={mobileMenuRef}
            className="md:hidden border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
          >
            <div className="px-4 py-3 space-y-3">
              {/* User Info */}
              {isAuthenticated && (
                <div className="pb-3 border-b border-gray-200 dark:border-gray-700">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {t('auth:welcome', { name: '' })}
                  </p>
                  <p className="font-medium text-gray-900 dark:text-white">{user?.name}</p>
                </div>
              )}

              {/* Navigation Links */}
              <Link
                to="/app"
                className={`flex items-center gap-3 px-3 py-3 rounded-lg transition-colors min-h-[48px] active:scale-[0.98] ${
                  location.pathname === '/app'
                    ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-200'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
                {t('dashboard:title')}
              </Link>

              {/* Mobile Controls */}
              <div className="flex flex-wrap gap-2 py-2">
                <VoiceToggle />
                <AIModelSelector />
                <LanguageSwitcher />
                {canAccessConfig && (
                  <button
                    onClick={() => {
                      setIsConfigOpen(true);
                      setIsMobileMenuOpen(false);
                    }}
                    className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                    title={t('common:config.title', 'System Configuration')}
                  >
                    <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </button>
                )}
              </div>

              {/* Login/Logout */}
              <div className="pt-3 border-t border-gray-200 dark:border-gray-700 pb-safe">
                {isAuthenticated ? (
                  <button
                    onClick={handleLogout}
                    className="w-full px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors min-h-[48px] active:scale-[0.98]"
                  >
                    {t('auth:logout')}
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      onLoginClick();
                      setIsMobileMenuOpen(false);
                    }}
                    className="w-full px-4 py-3 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors min-h-[48px] active:scale-[0.98]"
                  >
                    {t('auth:login')}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Config Dialog */}
      <ConfigDialog isOpen={isConfigOpen} onClose={() => setIsConfigOpen(false)} />
    </>
  );
};

// Home Page Component (Login prompt page) - now redirects to /app
const HomePage: React.FC<{ onLoginClick: () => void }> = ({ onLoginClick }) => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation(['auth', 'dashboard']);

  // If already authenticated, redirect to dashboard
  React.useEffect(() => {
    if (isAuthenticated) {
      navigate('/app');
    }
  }, [isAuthenticated, navigate]);

  return (
    <div className="space-y-8">
      {/* Welcome Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md dark:shadow-gray-700 p-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
          {t('dashboard:welcomeSystem')}
        </h2>
        <p className="mt-2 text-gray-600 dark:text-gray-400">
          {t('auth:pleaseLogin')}
        </p>
      </div>

      {/* Login Prompt */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md dark:shadow-gray-700 p-8 text-center">
        <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{t('auth:authRequired')}</h3>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          {t('auth:clickToLogin')}
        </p>
        <button
          onClick={onLoginClick}
          className="px-6 py-3 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
        >
          {t('auth:login')}
        </button>
        <div className="flex justify-center flex-wrap gap-4 text-sm text-gray-500 dark:text-gray-400 mt-6">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-purple-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            {t('dashboard:features.workShiftRecord')}
          </div>
        </div>
      </div>
    </div>
  );
};

// Login Page Component (standalone page version)
const LoginPage: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (isAuthenticated) {
      navigate('/app', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // If already authenticated, don't render the modal
  if (isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[var(--navy-deep)] flex items-center justify-center">
      <LoginModal
        isOpen={true}
        onClose={() => navigate('/')}
        onSuccess={() => navigate('/app', { replace: true })}
      />
    </div>
  );
};

// App Layout - wraps protected dashboard routes
interface AppLayoutProps {
  children: React.ReactNode;
  onLoginClick: () => void;
  editingSim: ISimCard | null;
  onCloseEdit: () => void;
  onEditSuccess: (sim: ISimCard) => void;
}

const AppLayout: React.FC<AppLayoutProps> = ({
  children,
  onLoginClick,
  editingSim,
  onCloseEdit,
  onEditSuccess,
}) => {
  const { t } = useTranslation('dashboard');

  const location = useLocation();

  // Determine if we're on a detail page (not dashboard)
  const isDetailPage = location.pathname !== '/app';

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 transition-colors">
      {/* Header */}
      <NavigationHeader onLoginClick={onLoginClick} />

      {/* Sidebar - only show on detail pages */}
      {isDetailPage && <AppSidebar />}

      {/* Main Content - add left margin when sidebar is visible */}
      <main className={`max-w-screen-2xl mx-auto py-4 px-3 sm:py-6 sm:px-6 lg:px-10 ${isDetailPage ? 'ml-14' : ''}`}>
        {children}
      </main>

      {/* Edit Modal */}
      {editingSim && (
        <SimCardEditModal
          sim={editingSim}
          isOpen={true}
          onClose={onCloseEdit}
          onSuccess={onEditSuccess}
        />
      )}

      {/* Toast Notifications */}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 3000,
          style: {
            background: '#333',
            color: '#fff',
          },
        }}
      />

      {/* Footer */}
      <footer className={`bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 mt-8 sm:mt-12 pb-safe ${isDetailPage ? 'ml-14' : ''}`}>
        <div className="max-w-screen-2xl mx-auto py-3 px-3 sm:py-4 sm:px-6 lg:px-10">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-2 text-xs sm:text-sm text-gray-500 dark:text-gray-400">
            <div>v{packageJson.version}</div>
            <div className="text-center">{t('footer.copyright', { year: new Date().getFullYear() })}</div>
            <div className="hidden sm:block invisible">v{packageJson.version}</div>
          </div>
        </div>
      </footer>
    </div>
  );
};

// Global Pomodoro widget - rendered once outside <Routes> so it persists across navigation
const GlobalPomodoroWidget: React.FC = () => {
  const location = useLocation();
  // Only show on authenticated /app routes
  if (!location.pathname.startsWith('/app')) return null;
  return <PomodoroWidget />;
};

// Inner App component that uses auth context
function AppContent() {
  const [editingSim, setEditingSim] = useState<ISimCard | null>(null);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);

  const handleEditSim = (sim: ISimCard) => {
    setEditingSim(sim);
  };

  const handleCloseEdit = () => {
    setEditingSim(null);
  };

  const handleEditSuccess = (updatedSim: ISimCard) => {
    setEditingSim(null);
  };

  return (
    <Router>
      <CartProvider>
      <PomodoroProvider>
      <Routes>
        {/* Landing Page - Public entry point */}
        <Route path="/" element={<LandingPage />} />

        {/* Login Page */}
        <Route path="/login" element={<LoginPage />} />

        {/* Protected App Routes */}
        <Route
          path="/app"
          element={
            <AppLayout
              onLoginClick={() => setIsLoginModalOpen(true)}
              editingSim={editingSim}
              onCloseEdit={handleCloseEdit}
              onEditSuccess={handleEditSuccess}
            >
              <Dashboard />
            </AppLayout>
          }
        />

        <Route
          path="/app/sims"
          element={
            <AppLayout
              onLoginClick={() => setIsLoginModalOpen(true)}
              editingSim={editingSim}
              onCloseEdit={handleCloseEdit}
              onEditSuccess={handleEditSuccess}
            >
              <SimCardListRoute onEdit={handleEditSim} />
            </AppLayout>
          }
        />

        <Route
          path="/app/sim/:iccid"
          element={
            <AppLayout
              onLoginClick={() => setIsLoginModalOpen(true)}
              editingSim={editingSim}
              onCloseEdit={handleCloseEdit}
              onEditSuccess={handleEditSuccess}
            >
              <SimCardDetailRoute onEdit={handleEditSim} />
            </AppLayout>
          }
        />

        <Route
          path="/app/modems"
          element={
            <AppLayout
              onLoginClick={() => setIsLoginModalOpen(true)}
              editingSim={editingSim}
              onCloseEdit={handleCloseEdit}
              onEditSuccess={handleEditSuccess}
            >
              <ModemList />
            </AppLayout>
          }
        />

        <Route
          path="/app/work"
          element={
            <AppLayout
              onLoginClick={() => setIsLoginModalOpen(true)}
              editingSim={editingSim}
              onCloseEdit={handleCloseEdit}
              onEditSuccess={handleEditSuccess}
            >
              <WorkContainer />
            </AppLayout>
          }
        />

        <Route
          path="/app/work/:tab"
          element={
            <AppLayout
              onLoginClick={() => setIsLoginModalOpen(true)}
              editingSim={editingSim}
              onCloseEdit={handleCloseEdit}
              onEditSuccess={handleEditSuccess}
            >
              <WorkContainer />
            </AppLayout>
          }
        />

        <Route
          path="/app/psi"
          element={
            <AppLayout
              onLoginClick={() => setIsLoginModalOpen(true)}
              editingSim={editingSim}
              onCloseEdit={handleCloseEdit}
              onEditSuccess={handleEditSuccess}
            >
              <PSIContainer />
            </AppLayout>
          }
        />

        <Route
          path="/app/psi/:tab"
          element={
            <AppLayout
              onLoginClick={() => setIsLoginModalOpen(true)}
              editingSim={editingSim}
              onCloseEdit={handleCloseEdit}
              onEditSuccess={handleEditSuccess}
            >
              <PSIContainer />
            </AppLayout>
          }
        />

        <Route
          path="/app/psi/:tab/:subtab"
          element={
            <AppLayout
              onLoginClick={() => setIsLoginModalOpen(true)}
              editingSim={editingSim}
              onCloseEdit={handleCloseEdit}
              onEditSuccess={handleEditSuccess}
            >
              <PSIContainer />
            </AppLayout>
          }
        />

        {/* BOM Routes - more specific routes first */}
        {/* PCB Module routes */}
        <Route
          path="/app/bom/pcb-modules/:productId/edit"
          element={
            <AppLayout
              onLoginClick={() => setIsLoginModalOpen(true)}
              editingSim={editingSim}
              onCloseEdit={handleCloseEdit}
              onEditSuccess={handleEditSuccess}
            >
              <ProductEdit />
            </AppLayout>
          }
        />

        <Route
          path="/app/bom/pcb-modules/:productId/transition"
          element={
            <AppLayout
              onLoginClick={() => setIsLoginModalOpen(true)}
              editingSim={editingSim}
              onCloseEdit={handleCloseEdit}
              onEditSuccess={handleEditSuccess}
            >
              <PhaseTransition />
            </AppLayout>
          }
        />

        <Route
          path="/app/bom/pcb-modules/:productId"
          element={
            <AppLayout
              onLoginClick={() => setIsLoginModalOpen(true)}
              editingSim={editingSim}
              onCloseEdit={handleCloseEdit}
              onEditSuccess={handleEditSuccess}
            >
              <ProductDetail />
            </AppLayout>
          }
        />

        {/* Legacy product routes - redirect to pcb-modules for backwards compatibility */}
        <Route
          path="/app/bom/products/:productId/edit"
          element={
            <AppLayout
              onLoginClick={() => setIsLoginModalOpen(true)}
              editingSim={editingSim}
              onCloseEdit={handleCloseEdit}
              onEditSuccess={handleEditSuccess}
            >
              <ProductEdit />
            </AppLayout>
          }
        />

        <Route
          path="/app/bom/products/:productId/transition"
          element={
            <AppLayout
              onLoginClick={() => setIsLoginModalOpen(true)}
              editingSim={editingSim}
              onCloseEdit={handleCloseEdit}
              onEditSuccess={handleEditSuccess}
            >
              <PhaseTransition />
            </AppLayout>
          }
        />

        <Route
          path="/app/bom/products/:productId"
          element={
            <AppLayout
              onLoginClick={() => setIsLoginModalOpen(true)}
              editingSim={editingSim}
              onCloseEdit={handleCloseEdit}
              onEditSuccess={handleEditSuccess}
            >
              <ProductDetail />
            </AppLayout>
          }
        />

        {/* Assembled Module routes */}
        <Route
          path="/app/bom/assembled-modules/:moduleId"
          element={
            <AppLayout
              onLoginClick={() => setIsLoginModalOpen(true)}
              editingSim={editingSim}
              onCloseEdit={handleCloseEdit}
              onEditSuccess={handleEditSuccess}
            >
              <BOMPage />
            </AppLayout>
          }
        />

        <Route
          path="/app/bom/:tab"
          element={
            <AppLayout
              onLoginClick={() => setIsLoginModalOpen(true)}
              editingSim={editingSim}
              onCloseEdit={handleCloseEdit}
              onEditSuccess={handleEditSuccess}
            >
              <BOMPage />
            </AppLayout>
          }
        />

        <Route
          path="/app/bom"
          element={
            <AppLayout
              onLoginClick={() => setIsLoginModalOpen(true)}
              editingSim={editingSim}
              onCloseEdit={handleCloseEdit}
              onEditSuccess={handleEditSuccess}
            >
              <BOMPage />
            </AppLayout>
          }
        />

        {/* MES (Manufacturing Execution System) Routes */}
        <Route
          path="/app/mes/orders"
          element={
            <AppLayout
              onLoginClick={() => setIsLoginModalOpen(true)}
              editingSim={editingSim}
              onCloseEdit={handleCloseEdit}
              onEditSuccess={handleEditSuccess}
            >
              <MOListPage />
            </AppLayout>
          }
        />

        <Route
          path="/app/mes/orders/:moId"
          element={
            <AppLayout
              onLoginClick={() => setIsLoginModalOpen(true)}
              editingSim={editingSim}
              onCloseEdit={handleCloseEdit}
              onEditSuccess={handleEditSuccess}
            >
              <MODetailPage />
            </AppLayout>
          }
        />

        <Route
          path="/app/mes"
          element={<Navigate to="/app/mes/orders" replace />}
        />

        {/* DocFlow (Document Processing Workflows) Routes */}
        <Route
          path="/app/docflow"
          element={
            <AppLayout
              onLoginClick={() => setIsLoginModalOpen(true)}
              editingSim={editingSim}
              onCloseEdit={handleCloseEdit}
              onEditSuccess={handleEditSuccess}
            >
              <WorkflowListPage />
            </AppLayout>
          }
        />

        <Route
          path="/app/docflow/:id/edit"
          element={
            <AppLayout
              onLoginClick={() => setIsLoginModalOpen(true)}
              editingSim={editingSim}
              onCloseEdit={handleCloseEdit}
              onEditSuccess={handleEditSuccess}
            >
              <WorkflowEditorPage />
            </AppLayout>
          }
        />

        <Route
          path="/app/docflow/:id/run"
          element={
            <AppLayout
              onLoginClick={() => setIsLoginModalOpen(true)}
              editingSim={editingSim}
              onCloseEdit={handleCloseEdit}
              onEditSuccess={handleEditSuccess}
            >
              <WorkflowRunPage />
            </AppLayout>
          }
        />

        <Route
          path="/app/docflow/executions/:id"
          element={
            <AppLayout
              onLoginClick={() => setIsLoginModalOpen(true)}
              editingSim={editingSim}
              onCloseEdit={handleCloseEdit}
              onEditSuccess={handleEditSuccess}
            >
              <ExecutionResultsPage />
            </AppLayout>
          }
        />

        {/* WebFlash (MCU Programmer) Route */}
        <Route
          path="/app/webflash"
          element={
            <AppLayout
              onLoginClick={() => setIsLoginModalOpen(true)}
              editingSim={editingSim}
              onCloseEdit={handleCloseEdit}
              onEditSuccess={handleEditSuccess}
            >
              <WebFlashPage />
            </AppLayout>
          }
        />

        {/* Mistake Notebook Route */}
        <Route
          path="/app/mistake-notebook"
          element={
            <AppLayout
              onLoginClick={() => setIsLoginModalOpen(true)}
              editingSim={editingSim}
              onCloseEdit={handleCloseEdit}
              onEditSuccess={handleEditSuccess}
            >
              <MistakeNotebookPage />
            </AppLayout>
          }
        />

        {/* Pomodoro Timer Route */}
        <Route
          path="/app/pomodoro"
          element={
            <AppLayout
              onLoginClick={() => setIsLoginModalOpen(true)}
              editingSim={editingSim}
              onCloseEdit={handleCloseEdit}
              onEditSuccess={handleEditSuccess}
            >
              <PomodoroPage />
            </AppLayout>
          }
        />

        {/* Project Management Routes */}
        <Route
          path="/app/projects"
          element={
            <AppLayout
              onLoginClick={() => setIsLoginModalOpen(true)}
              editingSim={editingSim}
              onCloseEdit={handleCloseEdit}
              onEditSuccess={handleEditSuccess}
            >
              <ProjectContainer />
            </AppLayout>
          }
        />

        <Route
          path="/app/projects/:view"
          element={
            <AppLayout
              onLoginClick={() => setIsLoginModalOpen(true)}
              editingSim={editingSim}
              onCloseEdit={handleCloseEdit}
              onEditSuccess={handleEditSuccess}
            >
              <ProjectContainer />
            </AppLayout>
          }
        />

        <Route
          path="/app/projects/:projectId/:view"
          element={
            <AppLayout
              onLoginClick={() => setIsLoginModalOpen(true)}
              editingSim={editingSim}
              onCloseEdit={handleCloseEdit}
              onEditSuccess={handleEditSuccess}
            >
              <ProjectContainer />
            </AppLayout>
          }
        />

        {/* TTQS routes under project - /app/projects/:projectId/ttqs/:tab */}
        <Route
          path="/app/projects/:projectId/ttqs/:tab"
          element={
            <AppLayout
              onLoginClick={() => setIsLoginModalOpen(true)}
              editingSim={editingSim}
              onCloseEdit={handleCloseEdit}
              onEditSuccess={handleEditSuccess}
            >
              <ProjectContainer />
            </AppLayout>
          }
        />

        <Route
          path="/app/camera"
          element={
            <AppLayout
              onLoginClick={() => setIsLoginModalOpen(true)}
              editingSim={editingSim}
              onCloseEdit={handleCloseEdit}
              onEditSuccess={handleEditSuccess}
            >
              <CameraPage />
            </AppLayout>
          }
        />

        <Route
          path="/app/barcode-test"
          element={
            <AppLayout
              onLoginClick={() => setIsLoginModalOpen(true)}
              editingSim={editingSim}
              onCloseEdit={handleCloseEdit}
              onEditSuccess={handleEditSuccess}
            >
              <BarcodeTest />
            </AppLayout>
          }
        />

        <Route
          path="/app/bluetooth-test"
          element={
            <AppLayout
              onLoginClick={() => setIsLoginModalOpen(true)}
              editingSim={editingSim}
              onCloseEdit={handleCloseEdit}
              onEditSuccess={handleEditSuccess}
            >
              <BluetoothTest />
            </AppLayout>
          }
        />

        <Route
          path="/app/owner/:owner"
          element={
            <AppLayout
              onLoginClick={() => setIsLoginModalOpen(true)}
              editingSim={editingSim}
              onCloseEdit={handleCloseEdit}
              onEditSuccess={handleEditSuccess}
            >
              <OwnerSummary />
            </AppLayout>
          }
        />

        <Route
          path="/app/owner/sim/:iccid"
          element={
            <AppLayout
              onLoginClick={() => setIsLoginModalOpen(true)}
              editingSim={editingSim}
              onCloseEdit={handleCloseEdit}
              onEditSuccess={handleEditSuccess}
            >
              <OwnerDetail />
            </AppLayout>
          }
        />

        {/* Agent Chat Room via MQTT (Admin) */}
        <Route
          path="/app/chat"
          element={
            <AppLayout
              onLoginClick={() => setIsLoginModalOpen(true)}
              editingSim={editingSim}
              onCloseEdit={handleCloseEdit}
              onEditSuccess={handleEditSuccess}
            >
              <MqttChatViewer />
            </AppLayout>
          }
        />

        {/* LINE Chat Monitor (Admin) */}
        <Route
          path="/app/line"
          element={
            <AppLayout
              onLoginClick={() => setIsLoginModalOpen(true)}
              editingSim={editingSim}
              onCloseEdit={handleCloseEdit}
              onEditSuccess={handleEditSuccess}
            >
              <LineChatMonitor />
            </AppLayout>
          }
        />

        {/* Blog Management (Admin) */}
        <Route
          path="/app/blog"
          element={
            <AppLayout
              onLoginClick={() => setIsLoginModalOpen(true)}
              editingSim={editingSim}
              onCloseEdit={handleCloseEdit}
              onEditSuccess={handleEditSuccess}
            >
              <BlogManagement />
            </AppLayout>
          }
        />

        {/* Product routes for landing page */}
        <Route path="/products/:category" element={<ProductCategoryPage />} />

        {/* E-Commerce routes (public) */}
        <Route path="/cart" element={<CartPage />} />
        <Route path="/checkout" element={<CheckoutPage />} />
        <Route path="/checkout/success" element={<CheckoutSuccessPage />} />

        {/* Public Blog Detail Page */}
        <Route path="/blog/:slug" element={<BlogDetailPage />} />

        {/* Catch all - redirect to Landing */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {/* Pomodoro Floating Widget - persists across route changes */}
      <GlobalPomodoroWidget />

      {/* Flow Button Layer - floating buttons that persist across route changes */}
      <FlowButtonLayer />

      {/* Login Modal (for header login button) */}
      <LoginModal
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
        onSuccess={() => setIsLoginModalOpen(false)}
      />
      </PomodoroProvider>
      </CartProvider>
    </Router>
  );
}

// Main App component with providers
function App() {
  return (
    <GoogleOAuthProvider clientId={getEnv('VITE_GOOGLE_OAUTH_CLIENT_ID')}>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <AuthProvider>
            <EmployeeProvider>
              <AIModelProvider>
                <WebRTCProvider>
                  <AppContent />
                  <ReactQueryDevtools initialIsOpen={false} />
                </WebRTCProvider>
              </AIModelProvider>
            </EmployeeProvider>
          </AuthProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </GoogleOAuthProvider>
  );
}

// Route component for SIM list
const SimCardListRoute: React.FC<{ onEdit: (sim: ISimCard) => void }> = ({ onEdit }) => {
  const handleSelectSim = (sim: ISimCard) => {
    window.location.href = `/app/sim/${sim.iccid}`;
  };

  return <SimCardList onSelectSim={handleSelectSim} />;
};

// Route component for SIM detail with URL params
const SimCardDetailRoute: React.FC<{ onEdit: (sim: ISimCard) => void }> = ({ onEdit }) => {
  const url = new URL(window.location.href);
  const iccid = url.pathname.split('/').pop() || '';

  if (!iccid) {
    return <Navigate to="/app/sims" replace />;
  }

  return (
    <SimCardDetail
      iccid={iccid}
      onBack={() => window.history.back()}
      onEdit={onEdit}
    />
  );
};

export default App;